/**
 * Data Provider personalizado para manejar el formato de respuesta del backend NestJS
 * Backend retorna: { data: [], total: number }
 * Refine espera: { data: [], total: number } (ya es compatible, pero necesita configuración)
 */

import { DataProvider } from "@pankod/refine-core";
import { AxiosInstance } from "@pankod/refine-simple-rest/node_modules/axios";
import { stringify } from "query-string";

export const dataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance
): DataProvider => ({
  getList: async ({ resource, hasPagination = true, pagination, filters, sort }) => {
    const url = `${apiUrl}/${resource}`;

    const { current = 1, pageSize = 10 } = pagination ?? {};

    const queryFilters: any = {};

    if (filters) {
      filters.forEach((filter) => {
        if (filter.operator === "eq") {
          if (filter.value !== null && filter.value !== undefined && filter.value !== "") {
            queryFilters[filter.field] = filter.value;
          }
        }
      });
    }

    const query: {
      _start?: number;
      _end?: number;
      _sort?: string;
      _order?: string;
    } = hasPagination
      ? {
          _start: (current - 1) * pageSize,
          _end: current * pageSize,
        }
      : {};

    if (sort && sort.length > 0) {
      query._sort = sort[0].field;
      query._order = sort[0].order;
    }

    const combinedQuery = { ...query, ...queryFilters };
    const { data } = await httpClient.get(
      `${url}?${stringify(combinedQuery)}`
    );

    // El backend retorna { data: [], total: number }
    // Refine espera { data: [], total: number }
    return {
      data: data.data || data, // Si data.data existe, usarlo, sino usar data directamente
      total: data.total || data.length, // Si total existe, usarlo, sino usar la longitud del array
    };
  },

  getMany: async ({ resource, ids }) => {
    const url = `${apiUrl}/${resource}`;

    const { data } = await httpClient.get(
      `${url}?${stringify({ id: ids })}`
    );

    return {
      data: data.data || data,
    };
  },

  getOne: async ({ resource, id }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    const { data } = await httpClient.get(url);

    // Si el backend retorna { data: {...} }, extraer el objeto interno
    return {
      data: data.data || data,
    };
  },

  create: async ({ resource, variables }) => {
    const url = `${apiUrl}/${resource}`;

    const { data } = await httpClient.post(url, variables);

    return {
      data: data.data || data,
    };
  },

  createMany: async ({ resource, variables }) => {
    const url = `${apiUrl}/${resource}/bulk`;

    const { data } = await httpClient.post(url, { bulk: variables });

    return {
      data: data.data || data,
    };
  },

  update: async ({ resource, id, variables }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    const { data } = await httpClient.patch(url, variables);

    return {
      data: data.data || data,
    };
  },

  updateMany: async ({ resource, ids, variables }) => {
    const responses = await Promise.all(
      ids.map((id) =>
        httpClient.patch(`${apiUrl}/${resource}/${id}`, variables)
      )
    );

    return {
      data: responses.map((response) => response.data.data || response.data),
    };
  },

  deleteOne: async ({ resource, id }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    const { data } = await httpClient.delete(url);

    return {
      data: data.data || data,
    };
  },

  deleteMany: async ({ resource, ids }) => {
    const responses = await Promise.all(
      ids.map((id) => httpClient.delete(`${apiUrl}/${resource}/${id}`))
    );

    return {
      data: responses.map((response) => response.data.data || response.data),
    };
  },

  getApiUrl: () => apiUrl,

  custom: async ({ url, method, filters, sort, payload, query, headers }) => {
    let requestUrl = `${url}?`;

    if (sort && sort.length > 0) {
      const sortQuery = {
        _sort: sort[0].field,
        _order: sort[0].order,
      };
      requestUrl = `${requestUrl}&${stringify(sortQuery)}`;
    }

    if (filters) {
      const filterQuery = filters.reduce((acc: any, filter) => {
        if (filter.operator === "eq") {
          acc[filter.field] = filter.value;
        }
        return acc;
      }, {});
      requestUrl = `${requestUrl}&${stringify(filterQuery)}`;
    }

    if (query) {
      requestUrl = `${requestUrl}&${stringify(query)}`;
    }

    const { data } = await httpClient({
      url: requestUrl,
      method,
      data: payload,
      headers,
    });

    return {
      data: data.data || data,
    };
  },
});
